import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from "@angular/router";
import { Subscription, forkJoin } from "rxjs";
import {
  SubgroupService,
  SubcategoryService,
  CategoryService,
  QuestionService,
  TaxReturnService,
  TaxReturnTypeService,
  QuestionAssociation,
  Category,
} from "../../../../swaggerServices";
import { cloneDeep } from "lodash-es";
import { groupBy } from "lodash-es";

import { ICategory, ISubcategory, ISubGroupQuestion, ISelectedCategory, ISelectedSubcategory, ISelectedSubgroup, ITabQuestion } from './tax-return-builder';
import { TaxReturnBuilderService } from './tax-return-builder.service';
import { map } from 'rxjs/operators';
import { ISubGroup } from '../../tax-returns/tax-return/subcategory/subcategory';
import { ConfigStateService } from "../../config/config-state.service";
import { Tenant } from "../../../shared/types/tenant.enum";
import { environment } from '../../../environments/environment';
import { TenantConfigService } from '../../../shared/services/tenant-config.service';
import { AbstractControl, UntypedFormGroup, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';

declare var $: any;

@Component({
    selector: 'app-tax-return-builder',
    templateUrl: './tax-return-builder.component.html',
    styleUrls: ['./tax-return-builder.component.scss'],
    standalone: false
})
export class TaxReturnBuilderComponent implements OnInit, OnDestroy {
  isReady: boolean;
  activeTab: 'categories' | 'sub-categories' | 'subgroups' | 'questions';
  categories: ICategory[];
  subcategories: ISubcategory[];
  @ViewChild('newCategoryModal') newCategoryModal: ElementRef;
  @ViewChild('editModal') editModal: ElementRef;
  @ViewChild('newSubcategoryModal') newSubcategoryModal: ElementRef;
  @ViewChild('newSubGroupModal') newSubGroupModal: ElementRef;
  @ViewChild('selectTabQuestionModal') selectTabQuestionModal: ElementRef;
  @ViewChild('addQuestionToSubgroupModal') addQuestionToSubgroupModal: ElementRef;
  @ViewChild('confirmDeleteModal') confirmDeleteModal: ElementRef;
  categorySearch: string;
  subcategorySearch: string;
  newCategoryName: string;
  newCategoryOrder: number;
  newSubcategoryName: string;
  newSubcategoryOrder: number;
  subgroups: ISubGroup[];
  subgroupSearch: string;
  newSubGroupName: string;
  newSubGroupHash: string;
  newSubGroupOrder: number;
  newSubGroupFolderPath: string;
  newSubGroupDocRegexPattern: string;
  originalQuestions: ISubGroupQuestion[];
  questions: ISubGroupQuestion[];
  questionSearch: string;
  selectTabQuestionSearch: string;
  addSubgrupQuestionSearch: string;
  selectedNode: {
    type?: 'Category' | 'Subcategory' | 'Subgroup' | 'Question'
    category?: ISelectedCategory
    subCategory?: ISelectedSubcategory
    subGroup?: ISelectedSubgroup
    question?: {
      id: number
      question: string
      newQuestionOrder: number
      order?: number
      subGroupOrder?: {
        subgroupId?: number
      }
    },
  };
  originalCategories: ICategory[];
  isSaving: boolean;
  isSyncingCache: boolean = false;
  taxYear: number;
  taxReturnTypeId: number;
  taxReturnTypeName: string;
  showQuestionId: boolean;
  allowAutoDocLink: boolean;
  uiConfig: TenantConfigService;
  taxReturnAlias: string;
  isProductionMode: boolean;
  debugModeControl: UntypedFormControl = new UntypedFormControl();
  debug = !!this.debugModeControl.value//false; // true to show hidden questions
  expandControl: UntypedFormControl = new UntypedFormControl();
  originalCategoriesMapById: Map<number, Category> = new Map();
  originalSubcategoriesMapByOriginalCatId: Map<number, Map<number, ISelectedSubcategory>> = new Map();

  disableMove: boolean;
  errorMessage: string;

  tabSubCategoryId: Set<any>;
  tabSubGroupId: Set<any>;
  isColumnQuestion: boolean;
  subgroupContainsTabQuestion: boolean;

  searchForm: UntypedFormGroup = new UntypedFormGroup({});
  newCategoryOptions: any[];
  newSubCategoryOptions: any[];
  allSubCategoryOptions: any[];
  newSubGroupOptions: any[];
  allSubGroupOptions: any[];

  private subscriptions: Subscription[] = [];

  constructor(private categoryService: CategoryService,
    private subcategoryService: SubcategoryService,
    private subgroupService: SubgroupService,
    private questionService: QuestionService,
    private taxReturnService: TaxReturnService,
    private activatedRoute: ActivatedRoute,
    private config: ConfigStateService,
    private taxReturnBuilderService: TaxReturnBuilderService,
    private typeService: TaxReturnTypeService,
    private formBuilder: UntypedFormBuilder) {
    this.isProductionMode = environment.production;
  }

  ngOnInit() {
    this.initForm();
    this.subscriptions.push(this.config.loadConfig().subscribe((config) => {
      this.showQuestionId = config.tenant !== Tenant.AU;
      this.allowAutoDocLink = config.featureFlags.allowAutoDocLink;
    }));
    this.subscriptions.push(this.activatedRoute.queryParams.subscribe(
      params => {
        if (params['tab']) this.activeTab = params['tab'];
        this.taxYear = params['taxYear'];
        this.taxReturnTypeId = params['taxReturnTypeId'];
        this.init();
      }));
      this.subscriptions.push(
        this.config.loadConfig().subscribe(config => {
          this.uiConfig = new TenantConfigService(config);
          this.taxReturnAlias = this.uiConfig.config.taxReturnAlias
        })
      );
      this.subscriptions.push(this.debugModeControl.statusChanges.subscribe(newVal => {
        this.debug = this.debugModeControl.value;
        console.log(this.debug)
      }))
      this.subscriptions.push(this.expandControl.valueChanges.subscribe(newVal => {
        if(newVal){
          this.expandAll()
        } else {
          this.collapseAll()
        }
      }))
    this.initForm();
  };

  private initForm() {
    this.searchForm = this.formBuilder.group({
      newCategory: [''],
      newSubCategory: [''],
      newSubGroup: [''],
    });
  }

  init() {
    this.isSaving = true;
    this.expandControl.setValue(false);
    this.selectedNode = {};
    this.getTaxReturnType();
    this.getCategories();
    this.getQuestions();
    this.getSubcategories();
    this.getSubgroups();
  }

  get newSubGroup():AbstractControl {
    return this.searchForm.get('newSubGroup');
  }

  get newSubCategory():AbstractControl {
    return this.searchForm.get('newSubCategory');
  }

  get newCategory():AbstractControl {
    return this.searchForm.get('newCategory');
  }

  private getTaxReturnType() {
    const taxReturnTypeId = this.activatedRoute.queryParams['value'].taxReturnTypeId;
    const sub = this.typeService.getTaxReturnTypeById(taxReturnTypeId)
      .subscribe(result => {
        this.taxReturnTypeName = result.type;
      });
    this.subscriptions.push(sub);
  }

  /**
   * all about categories
   */

  private getCategories() {
    const taxReturnTypeId = this.activatedRoute.queryParams['value'].taxReturnTypeId;
    const sub = this.taxReturnBuilderService.getCategories(taxReturnTypeId)
      .subscribe(results => {
        this.categories = results;
        this.originalCategories = cloneDeep(this.categories);
        this.initAllCategoryDetails();
        this.isReady = true;
        this.isSaving = false;
        this.newCategoryOptions = results.map(r => {
          return {
            ...r,
            value: r.id
          }
        })

    });
    this.subscriptions.push(sub);
  }

  selectCategory(category, index) {
    this.loadCategory(index);
    this.editNode(category);
  }

  showCreateCategoryModal() {
    this.newCategoryName = null;
    this.newCategoryOrder = null;
    $(this.newCategoryModal.nativeElement).modal('show');
  }

  // when creating category, need to get new id from api in order to get id, all other list updates are handled locally
  addCategory() {
    const category = {
      id: null,
      name: this.newCategoryName,
      newCategoryOrder: this.newCategoryOrder,
      subcategories: []
    };
    // add to list
    this.categories.push(category);
    this.updateOrders({ category });
    $(this.newCategoryModal.nativeElement).modal('hide');
  }

  private initAllCategoryDetails() {
    this.categories.forEach((c, index) => {
      this.loadCategory(index);
    });
  }

  private loadCategory(index) {
    if (!this.categories[index] || this.categories[index].isLoaded) {
      return;
    }
    this.categories[index].isLoading = true;
    const taxYr = this.activatedRoute.queryParams['value'].taxYear;
    const sub = this.taxReturnBuilderService.getCategoryById(this.categories[index].id, taxYr).subscribe(result => {
      this.categories[index].subcategories = result.subcategories;
      this.categories[index].isLoading = false;
      this.categories[index].isLoaded = true;
      this.originalCategories = cloneDeep(this.categories);
      this.originalCategories.forEach(c => this.originalCategoriesMapById.set(c.id, c));
      this.originalCategories.forEach(item => {
        item.subcategories.forEach(subItem => {
          const subcatMapById = new Map();
            if (this.originalSubcategoriesMapByOriginalCatId.has(item.id)) {
              const originalCat = this.originalSubcategoriesMapByOriginalCatId.get(item.id);
              subcatMapById.set(subItem.id, subItem)
              this.originalSubcategoriesMapByOriginalCatId.set(item.id, new Map([...originalCat, ...subcatMapById]))
            } else this.originalSubcategoriesMapByOriginalCatId.set(item.id, subcatMapById.set(subItem.id, subItem))
          })})
    });

    this.subscriptions.push(sub);
  }

  /**
   * all about subcategories
   */
  private getSubcategories() {
    const taxReturnTypeId = this.activatedRoute.queryParams['value'].taxReturnTypeId;
    const sub = this.taxReturnBuilderService.getSubcategories(taxReturnTypeId).subscribe(results => {
      this.subcategories = results;
      this.allSubCategoryOptions = results.map(r => {
        return {
          ...r,
          value: r.id
        }
      })
      this.tabSubCategoryId = new Set();
    });

    this.subscriptions.push(sub);
  }

  showCreateSubcategoryModal() {
    this.newSubcategoryName = null;
    this.newSubcategoryOrder = null;
    $(this.newSubcategoryModal.nativeElement).modal('show');
  }

  createSubcategory() {
    const subCategory: ISubcategory = {
      id: null,
      name: this.newSubcategoryName,
      newOrder: this.newSubcategoryOrder,
      subgroups: [],
      tabQuestion: null
    };

    // add to list
    this.subcategories.push(subCategory);

    // add to tax structure
    this.selectedNode.category.subcategories.push(subCategory);
    this.updateOrders({ subCategory: subCategory });
    $(this.newSubcategoryModal.nativeElement).modal('hide');
    this.selectedNode.category.isCollapsed = false;
  }

  /** all about subgroups **/
  private getSubgroups() {
    const taxReturnTypeId = this.activatedRoute.queryParams['value'].taxReturnTypeId;
    const sub = this.taxReturnBuilderService.getSubgroups(taxReturnTypeId).subscribe(results => {
      this.subgroups = results;
      this.allSubGroupOptions = results.map(r => {
        return {
          ...r,
          value: r.id
        }
      })
      this.tabSubGroupId = new Set();
    });

    this.subscriptions.push(sub);
  }

  showCreateSubgroupModal() {
    this.newSubGroupName = null;
    this.newSubGroupFolderPath = null;
    this.newSubGroupDocRegexPattern = null;
    this.newSubGroupHash = null;
    this.newSubGroupOrder = null;
    $(this.newSubGroupModal.nativeElement).modal('show');
  }

  // add subgroup
  createSubgroup() {

    const subgroup = {
      name: this.newSubGroupName,
      folderPath: this.newSubGroupFolderPath,
      docRegexPattern: this.newSubGroupDocRegexPattern,
      hash: this.newSubGroupHash,
      newSubGroupOrder: this.newSubGroupOrder,
      questions: [],
    };

    // add to list
    this.subgroups.push(subgroup);
    // add tax structure
    this.selectedNode.subCategory.subgroups.push(subgroup);
    this.updateOrders({ subGroup: subgroup });
    // auto expand subcategory to reveal subgroup
    this.selectedNode.subCategory.isCollapsed = false;
    $(this.newSubGroupModal.nativeElement).modal('hide');

  }

  showTabQuestionModal() {
    $(this.selectTabQuestionModal.nativeElement).modal('show');
  }

  // a tab question must also be in a subgroup
  selectTabQuestion(q: ITabQuestion) {

    const isInSg = this.selectedNode.subCategory.subgroups.find(sg =>
      sg.questions.some(_q => _q.id === q.id));
    if (!isInSg) {
      return alert(`You must first add '${q.question}' to a subgroup within this subcategory before adding it as a tab question`);
    }

    this.selectedNode.subCategory.tabQuestion = {
      ...q,
      canEdit: true
    };
    $(this.selectTabQuestionModal.nativeElement).modal('hide');
  }

  showAddQuestionToSubgroupModal() {
    this.addSubgrupQuestionSearch = null;
    $(this.addQuestionToSubgroupModal.nativeElement).modal('show');
  }

  // filter out questions that haven't already been used
  canAddQuestion(q) {
    let usedQ;

    // when selected node is subcategory, and it's sg contains a tab q, it means we're now trying to add a tab question
    // filter questions for tab question within the subcategory's subgroups
    if (
      this.selectedNode.subCategory &&
      this.selectedNode.subCategory.subgroups.some(
        sg => !!sg.questions.find(_q => _q.isTab)
      )
    ) {
      usedQ = !(
        q.isTab &&
        this.selectedNode.subCategory.subgroups.some(
          sg => !!sg.questions.find(_q => _q.id === q.id)
        )
      );
    }
    // we don't want to add column questions on their own, they should only be added
    // when the table question (parent question) is added
    else if (q.parentId) {
      usedQ = true;
    } else {
      this.categories.forEach(c => {
        c.subcategories.forEach(sc => {
          // check it's not already used as a tab question
          if (sc.tabQuestion && sc.tabQuestion.id === q.id) usedQ = true;
          sc.subgroups && sc.subgroups.forEach(sg => {
            if (sg.questions.find(_q => _q.id === q.id)) usedQ = true;
          });
        })
      });
    }

    return !usedQ
  }

  // adds a question to a subgroup
  addQuestionToSubgroup(q: ISubGroupQuestion) {

    // find the associated subcategory (can't rely on id as it may be a new sc)
    const hasTabQuestion = () => {
      let subcategory: ISubcategory;
      this.categories.forEach(c => {
        c.subcategories.forEach(sc => {
          if (!sc.isCollapsed) {
            sc.subgroups.forEach(sg => {
              if (this.selectedNode.subGroup.id && this.selectedNode.subGroup.id === sg.id)
                subcategory = sc;
              else if (this.selectedNode.subGroup.name === sg.name)
                subcategory = sc;
            })
          }
        })
      });
      return subcategory && subcategory.subgroups.some(sg => sg.questions.some(q => q.isTab));
    };

    // for dependant mandatory question to be added
    if (q.dependantMandatoryParentId && !this.selectedNode.subGroup.questions.find(_q => _q.id === q.dependantMandatoryParentId)) {
      const depQ = this.questions.find(_q => _q.id === q.dependantMandatoryParentId);
      return alert("This question is dependant on the question '" + depQ.question + "'. You must first add that question to this subgroup.");
    }

    // if adding a column q, must add the table q
    else if (q.parentId) {
      if (!this.selectedNode.subGroup.questions.find(_q => _q.id === q.parentId)) {
        const tableQ = this.questions.find(_q => _q.id === q.parentId);
        return alert("This is a column question for the table '" + tableQ.question + "'. You must first add that question to this subgroup.");
      }
    }

    // limit 1 tab question per subcategory
    else if (q.isTab && hasTabQuestion()) {
      return alert("Only one tab question can be added per subcategory.");
    }

    else {

      q.order = this.selectedNode.subGroup.questions.length + 1;

      this.selectedNode.subGroup.questions.push({
        ...q,
        canRemove: true
      });
      // find all column questions and add to subgroup
      this.questions.forEach(_q => {
        if (_q.parentId === q.id) {
          this.selectedNode.subGroup.questions.push({
            ..._q,
            canRemove: true
          });
        }
      });

      this.updateOrders({ question: q });
      this.selectedNode.subGroup.isCollapsed = false;
      $(this.addQuestionToSubgroupModal.nativeElement).modal('hide');

    }

  }

  /** all about questions **/
  private getQuestions() {
    const taxYr = this.activatedRoute.queryParams['value'].taxYear;
    const taxReturnTypeId = this.activatedRoute.queryParams['value'].taxReturnTypeId;
    this.taxReturnBuilderService.getQuestionsByyear(taxYr, taxReturnTypeId).subscribe(results => {
      this.questions = results;
      this.originalQuestions = cloneDeep(this.questions);
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  getTaxYearParam() {
    return { taxYear: this.taxYear };
  }

  // whenever an order is updated via UI, need to update the order within the array structure as well
  private initOrders() {
    this.categories = this.categories
      .filter(c => c.order != null)
      .sort((a, b) => a.order - b.order)
      .concat(this.categories.filter(c => c.order == null));

    this.categories.forEach(c => {
      c.subcategories = c.subcategories
        .filter(sc => sc.order != null)
        .sort((a, b) => a.order - b.order)
        .concat(c.subcategories.filter(sc => sc.order == null));

      c.subcategories.forEach(sc => {
        sc.subgroups = sc.subgroups
          .filter(sg => sg.order != null)
          .sort((a, b) => a.order - b.order)
          .concat(sc.subgroups.filter(sg => sg.order == null));

        sc.subgroups.forEach(sg => {
          const qsByParentId = groupBy(sg.questions.filter(q => q.parentId), q => q.parentId);
          sg.questions = sg.questions
            .filter(q => q.order != null && q.parentId == null)
            .sort((a, b) => a.order - b.order)
            .concat(sg.questions.filter(q => q.order == null && q.parentId == null));
          for (const tableQuestionId of Object.keys(qsByParentId)) {
            let tableCols = qsByParentId[tableQuestionId];
            tableCols = tableCols
              .filter(q => q.order != null)
              .sort((a, b) => a.order - b.order)
              .concat(tableCols.filter(q => q.order == null));
            const tableQuestionIdNumber = Number(tableQuestionId);
            const index = sg.questions.findIndex(q => q.id === tableQuestionIdNumber);
            if (index >= 0) {
              sg.questions.splice(index + 1, 0, ...tableCols);
            }
          }
        })
      })
    });
  }

  editNode(category?, subCategory?, subGroup?, question?) {

    this.selectedNode = {};

    const isTabQ = q => q.isTab;
    const isTabIdQ = q => !q.parentId && q.isRowId;
    const isColumnQ = q => q.parentId;

    //find tabSubCategory and tabSubGroup
    if (this.tabSubCategoryId.size === 0) {
      let isTab;
      let tabSubCategory = [];
      let tabSubGroup = [];
      this.categories.forEach(c => {
        c.subcategories.forEach(sc => {
          isTab = this.getTabQuestion(sc);
          if(isTab !== 'Not tabbed') {
            tabSubCategory.push(sc.id);
            sc.subgroups.forEach(sg => {
              tabSubGroup.push(sg.id);
            })
          }
        })
      })
      this.tabSubCategoryId = new Set(tabSubCategory);
      this.tabSubGroupId = new Set(tabSubGroup);
    }

    if (category) {
      this.selectedNode.category = category;
      this.selectedNode.type = 'Category';
      category.isCollapsed = !category.isCollapsed;
    }

    else if (subCategory) {
      this.selectedNode.subCategory = subCategory;
      this.selectedNode.type = 'Subcategory';
      subCategory.isCollapsed = !subCategory.isCollapsed;
      this.disableMove = this.isProductionMode ;
    }

    else if (subGroup) {
      this.selectedNode.subGroup = subGroup;
      this.selectedNode.type = 'Subgroup';
      subGroup.isCollapsed = !subGroup.isCollapsed;
      this.subgroupContainsTabQuestion = subGroup.questions.some(q => q.isTab);

      // cannot move subgroup containing tab question
      this.disableMove = this.isProductionMode || (subGroup.questions.some(q => isTabQ(q) || isTabIdQ(q)));

      //If the current subGruop in tabSubcategory, it can be only moved to other Subcategory also in tabs
      if ( this.tabSubCategoryId.has(subGroup.subgroupSubcategoryOrder.subcategoryId) && !this.subgroupContainsTabQuestion) {
        this.newSubCategoryOptions = this.allSubCategoryOptions.filter(item =>
          this.tabSubCategoryId.has(item.id)
        )
      } else {
        this.newSubCategoryOptions = this.allSubCategoryOptions.filter(item =>
          !(this.tabSubCategoryId.has(item.id))
        )
      }
    }

    else if (question) {
      this.selectedNode.question = question;
      this.selectedNode.type = 'Question';

      // cannot move column questions, tab  or tab id questions
      this.disableMove = this.isProductionMode || (isColumnQ(question) || isTabQ(question) || isTabIdQ(question));
      // (production) and is column

      //If the current question in tabSubgroup, it can be only moved to other subgroups also in tabs
      if ( this.tabSubGroupId.has(question.subGroupOrder.subgroupId)) {
        if (question.isTab) {
          this.categories.forEach(c => {
            c.subcategories.forEach(sc => {
              if (sc.subgroups.find(sg => sg.id === question.subGroupOrder.subgroupId)) {
                this.newSubGroupOptions = this.allSubGroupOptions.filter(item =>
                  sc.subgroups.find(sg => sg.id === item.id)
                )
              }
            })
          })
        }else {
          this.newSubGroupOptions = this.allSubGroupOptions.filter(item =>
            this.tabSubGroupId.has(item.id)
          )
        }
      }else {
        this.newSubGroupOptions = this.allSubGroupOptions.filter(item =>
          !(this.tabSubGroupId.has(item.id))
        )
      }
    }

  }

  updateOrders(selectedNode) {
    if (selectedNode.category) {
      if (selectedNode.category.newCategoryName) {
        selectedNode.category.name = selectedNode.category.newCategoryName;
      }

      // ensure it takes precedence over other items with duplicate orders
      if (selectedNode.category.order !== selectedNode.category.newCategoryOrder) {
        const dup = this.categories.find(c => c.order === selectedNode.category.newCategoryOrder);
        if (dup) {
          const movingDown = selectedNode.category.newCategoryOrder > selectedNode.category.order;
          this.categories.forEach(c => {
            if (c.order < selectedNode.category.newCategoryOrder) {
              c.order--;
            } else if (c.order > selectedNode.category.newCategoryOrder) {
              c.order++;
            } else {
              c.order += movingDown ? -1 : 1;
            }
          });
        }
        selectedNode.category.order = selectedNode.category.newCategoryOrder;
        this.initOrders();
      }
    } else if (selectedNode.subCategory) {
      if (selectedNode.subCategory.newSubCategoryName) {
        selectedNode.subCategory.name = selectedNode.subCategory.newSubCategoryName;
      }
      if (selectedNode.subCategory.newCategoryId != null) {
        selectedNode.subCategory.categoryId = selectedNode.subCategory.newCategoryId;
      }

      //change categoryId in current subCategory object
      if (this.newCategory.value && this.newCategory.value !== selectedNode.subCategory.categoryId) {
        const originalCategoryId = selectedNode.subCategory.categoryId;
        selectedNode.subCategory.categoryId = this.newCategory.value;

        this.categories.forEach(c => {
          if (c.id === this.newCategory.value){
            c.subcategories.push(selectedNode.subCategory);
            selectedNode.subCategory.order = c.subcategories.length;
          }
          if (c.id === originalCategoryId) {
            c.subcategories.forEach((sc, i) => {
              if (sc.id === selectedNode.subCategory.id) {
                c.subcategories.splice(i, 1);
              }
            } )
          }
        })
      }

      // ensure it takes precedence over other items with duplicate orders
      if (selectedNode.subCategory.order !== selectedNode.subCategory.newOrder) {

        let category;
        this.categories.forEach(c => {
          if (selectedNode.subCategory.id) {
            if (c.subcategories.find(sc => sc.id === selectedNode.subCategory.id)) {
              category = c;
            }
          } else {
            if (c.subcategories.find(sc => sc.name === selectedNode.subCategory.name)) {
              category = c;
            }
          }
        });

        const dup = category.subcategories.find(sc => sc.order === selectedNode.subCategory.newOrder);
        if (dup) {
          const movingDown = selectedNode.subCategory.newOrder > selectedNode.subCategory.order;
          category.subcategories.forEach(sc => {
            if (sc.order < selectedNode.subCategory.newOrder) {
              sc.order--;
            } else if (sc.order > selectedNode.subCategory.newOrder) {
              sc.order++;
            } else {
              sc.order += movingDown ? -1 : 1;
            }
          });
        }
        if (selectedNode.subCategory.newOrder != null) {
          selectedNode.subCategory.order = selectedNode.subCategory.newOrder;
        }
        this.initOrders();
      }
    } else if (selectedNode.subGroup) {
      if (selectedNode.subGroup.newSubGroupName) {
        selectedNode.subGroup.name = selectedNode.subGroup.newSubGroupName;
      }
      if (selectedNode.subGroup.folderPath !== selectedNode.subGroup.newSubGroupFolderPath) {
        selectedNode.subGroup.folderPath = selectedNode.subGroup.newSubGroupFolderPath;
      }
      if (selectedNode.subGroup.docRegexPattern !== selectedNode.subGroup.newSubGroupDocRegexPattern) {
        selectedNode.subGroup.docRegexPattern = selectedNode.subGroup.newSubGroupDocRegexPattern;
      }
      if (selectedNode.subGroup.newSubGroupHash) {
        selectedNode.subGroup.hash = selectedNode.subGroup.newSubGroupHash;
      }

      //change subcategoryId in current subGroup object
      if (this.newSubCategory.value && selectedNode.subGroup.subgroupSubcategoryOrder && this.newSubCategory.value !== selectedNode.subGroup?.subgroupSubcategoryOrder?.subcategoryId) {
        const originalSubCategoryId = selectedNode.subGroup?.subgroupSubcategoryOrder?.subcategoryId;
        selectedNode.subGroup.subgroupSubcategoryOrder.subcategoryId = this.newSubCategory.value;

        this.categories.forEach(c => {
          c.subcategories.forEach(sc => {
            if (sc.id === this.newSubCategory.value){
              sc.subgroups.push(selectedNode.subGroup);
              selectedNode.subGroup.order = sc.subgroups.length;
            }

            if (sc.id === originalSubCategoryId) {
              sc.subgroups.forEach((sg, i) => {
                if (sg.id === selectedNode.subGroup.id) {
                  sc.subgroups.splice(i, 1);
                }
              } )
            }
          })
        })
      }

      // ensure it takes precedence over other items with duplicate orders
      if (selectedNode.subGroup.order !== selectedNode.subGroup.newSubGroupOrder) {
        let subCategory;
        this.categories.forEach(c => {
          c.subcategories.forEach(sc => {
            if (selectedNode.subGroup.id) {
              if (sc.subgroups.find(sg => sg.id === selectedNode.subGroup.id)) {
                subCategory = sc;
              }
            } else {
              if (sc.subgroups.find(sg => sg.name === selectedNode.subGroup.name)) {
                subCategory = sc;
              }
            }
          });
        });

        const dup = subCategory.subgroups.find(sc => sc.order === selectedNode.subGroup.newSubGroupOrder);
        if (dup) {
          const movingDown = selectedNode.subGroup.newSubGroupOrder > selectedNode.subGroup.order;
          subCategory.subgroups.forEach(sg => {
            if (sg.order < selectedNode.subGroup.newSubGroupOrder) {
              sg.order--;
            } else if (sg.order > selectedNode.subGroup.newSubGroupOrder) {
              sg.order++;
            } else {
              sg.order += movingDown ? -1 : 1;
            }
          });
        }
        selectedNode.subGroup.order = selectedNode.subGroup.newSubGroupOrder;
        this.initOrders();
      }
    } else if (selectedNode.question) {
      //change subgroupId in current question object
      if (this.newSubGroup.value && selectedNode.question.subGroupOrder && this.newSubGroup.value !== selectedNode.question.subGroupOrder.subgroupId) {
        const originalSubGroupId = selectedNode.question.subGroupOrder.subgroupId;
        selectedNode.question.subGroupOrder.subgroupId = this.newSubGroup.value;

        // if the quetion that needs to be moved is a table, find allColumnQuestion
        let allColumnQuestion = [];
        if (selectedNode.question.displayFieldType === 'table') {
          this.categories.forEach(c => {
            c.subcategories.forEach(sc => {
              sc.subgroups.forEach(sg => {
                if (sg.id === originalSubGroupId) {
                  sg.questions.forEach(q => {
                    if (q.parentId === selectedNode.question.id) {
                      allColumnQuestion.push(q)
                    }
                  })
                }
              })
            })
          })
        }

        // change subgroupId in current question object and all ColumnQuestion object
        this.categories.forEach(c => {
          c.subcategories.forEach(sc => {
            sc.subgroups.forEach(sg => {
              if (sg.id === this.newSubGroup.value){
                sg.questions.push(selectedNode.question);
                selectedNode.question.order = sg.questions.length;
                if(allColumnQuestion) {
                  allColumnQuestion.forEach(col => {
                    col.order = sg.questions.length;
                    sg.questions.push(col);
                  })
                }
              }

              if (sg.id === originalSubGroupId) {
                sg.questions = sg.questions.filter(q => q.id !== selectedNode.question.id && !(allColumnQuestion.includes(q)))
              }
            })
          })
        })
      }
      // ensure it takes precedence over other items with duplicate orders
      if (selectedNode.question.order !== selectedNode.question.newQuestionOrder) {
        let subGroup;
        this.categories.forEach(c => {
          c.subcategories.forEach(sc => {
            sc.subgroups.forEach(sg => {
              if (sg.questions.find(q => q.id === selectedNode.question.id)) {
                subGroup = sg;
              }
            });
          });
        });
        const dup = subGroup.questions.find(q => q.order === selectedNode.question.newQuestionOrder);
        if (dup) {
          const movingDown = selectedNode.question.newQuestionOrder > selectedNode.question.order;
          subGroup.questions.forEach(q => {
            if (q.order < selectedNode.question.newQuestionOrder) {
              q.order--;
            } else if (q.order > selectedNode.question.newQuestionOrder) {
              q.order++;
            } else {
              q.order += movingDown ? -1 : 1;
            }
          });
        }
        selectedNode.question.order = selectedNode.question.newQuestionOrder;
        this.initOrders();
      }
    }

    this.categories.forEach((c, cIndex) => {
      c.order = cIndex + 1;
      c.subcategories.forEach((sc, scIndex) => {
        sc.order = scIndex + 1;
        sc.subgroups.forEach((sg, sgIndex) => {
          sg.order = sgIndex + 1;
          sg.questions.forEach((q, qIndex) => {
            q.order = qIndex + 1;
          });
        });
      });
    });

  }

  showEditModal() {
    this.reset();

    $(this.editModal.nativeElement).modal({
      onDeny: () => {
        this.reset();
      },
      onApprove: () => {
        this.updateOrders(this.selectedNode);
      }
    }).modal('show');
  }

  reset() {
    this.errorMessage = null
    if (this.selectedNode.category) {
      this.selectedNode.category.newCategoryName = this.selectedNode.category.name;
      this.selectedNode.category.newCategoryOrder = this.selectedNode.category.order;
    }
    else if (this.selectedNode.subCategory) {
      this.selectedNode.subCategory.newSubCategoryName = this.selectedNode.subCategory.name;
      this.selectedNode.subCategory.newCategoryId = this.selectedNode.subCategory.categoryId;
      this.selectedNode.subCategory.newOrder = this.selectedNode.subCategory.order;
      this.newCategory.setValue(this.selectedNode.subCategory.categoryId)
    }
    else if (this.selectedNode.subGroup) {
      this.selectedNode.subGroup.newSubGroupName = this.selectedNode.subGroup.name;
      this.selectedNode.subGroup.newSubGroupFolderPath = this.selectedNode.subGroup.folderPath;
      this.selectedNode.subGroup.newSubGroupDocRegexPattern = this.selectedNode.subGroup.docRegexPattern;
      this.selectedNode.subGroup.newSubGroupHash = this.selectedNode.subGroup.hash;
      this.selectedNode.subGroup.newSubGroupOrder = this.selectedNode.subGroup.order;
      //not able to edit a subgroup which is in an unsaved subcategory, throw error
      if (!this.selectedNode.subGroup.subgroupSubcategoryOrder) {
        this.errorMessage = `Please save current structure before editing this subgroup`
      }else {
        this.newSubCategory.setValue(this.selectedNode.subGroup.subgroupSubcategoryOrder.subcategoryId)
      }
    }
    else if (this.selectedNode.question) {
      this.selectedNode.question.newQuestionOrder = this.selectedNode.question.order;
      if (!this.selectedNode.question.subGroupOrder) {
        this.errorMessage = `Please save current structure before editing this question`
      }else {
        this.newSubGroup.setValue(this.selectedNode.question.subGroupOrder.subgroupId)
      }
    }
  }

  // find the tab question for a subcategory
  getTabQuestion(subCategory) {
    let tabQuestion;
    subCategory.subgroups && subCategory.subgroups.forEach(sg => {
      sg.questions && sg.questions.forEach(q => {
        if (q.isTab) tabQuestion = q;
      })
    });
    return tabQuestion ? tabQuestion.question : 'Not tabbed';
  }

  getQuestionType(q: ISubGroupQuestion): string {
    let questionType = q.displayFieldType;
    if (q.displayFieldType === 'text') {
      if (q.dataType === 'string') {
        questionType = 'text';
      }
      if (q.dataType.toLowerCase() === 'integer' || q.dataType.toLowerCase() === 'float') {
        questionType = 'number';
      }
    }
    return questionType;
  };

  showConfirmDeleteModal(type: 'category' | 'subCategory' | 'subGroup' | 'question',
    item: ICategory | ISubcategory | ISubGroup | ISubGroupQuestion | any,
    subGroup?: ISubGroup) {

    $(this.confirmDeleteModal.nativeElement).modal({

      onApprove: () => {

        if (type === 'category') {
          this.categories = this.categories.filter(c =>
            // when adding multiple new items, can only compare against name
            c.name !== item.name);
        }

        if (type === 'subCategory') {
          this.categories.forEach(c =>
            c.subcategories = c.subcategories.filter(sc =>
              sc.id ? sc.id !== item.id : sc.name !== item.name));
        }

        else if (type === 'subGroup') {
          this.categories.forEach(c =>
            c.subcategories.forEach(sc =>
              sc.subgroups = sc.subgroups.filter(sg =>
                sg.id ? sg.id !== item.id : sg.name !== item.name)));
        }

        // when removing question, need to only remove it from relevant subgroup, as same q can be in multiple sg
        // if removing table question, also remove column questions
        else if (type === 'question') {
          subGroup.questions = subGroup.questions.filter(q =>
            q.id ? q.id !== item.id : q.question !== item.question);

          if (item.dataType === 'table') {
            subGroup.questions = subGroup.questions.filter(q => q.parentId !== item.id);
          }

          // when removing a tab question from a sg, also remove it from the sc
          if (item.isTab) {
            this.categories.forEach(c => {
              c.subcategories.forEach(sc => {
                if (sc.tabQuestion && sc.tabQuestion.id === item.id) sc.tabQuestion = null;
              })
            })
          }
        }
        this.updateOrders({});
      }
    }).modal('show');

  }

  syncCache() {
    this.isSyncingCache = true;
    this.taxReturnService.syncTaxReturnStructure().subscribe(() => {
      this.isSyncingCache = false;
    }, () => {
      this.isSyncingCache = false;
    });
  }

  // child items rely on parent item ids, need save new parent items before associating them to child items
  save() {
    this.isSaving = true;

    const categoryObs = []; // saving categories
    const categoryOrderObs = []; // saving category orders
    const subCategoryObs = [];
    const subCategoryOrderObs = [];
    const subcategoryTabQuestionObs = [];
    const subGroupObs = []; // creating new subgroups
    const subGroupAssociationObs = []; // associating newly created sg to a sc, and de-associating sg from sc
    const subGroupOrderObs = []; // saving subgroup order
    const questionToSubgroupObs = []; // associating q to and from sg
    const questionObs = []; // saving questions
    const questionOrderObs = [];

    const questionOrderObjects = [];
    const questionSubCatOrderObjects = [];

    // handle category changes
    const handleCategory = () => {

      this.categories.forEach((c) => {

        const originalCategory = this.originalCategoriesMapById.has(c.id) && this.originalCategoriesMapById.get(c.id)
        // new category
        if (!c.id) {
          categoryObs.push(this.categoryService.addCategory({
            name: c.name,
            taxReturnTypeId: this.taxReturnTypeId
          }).pipe(map((_c) => {
            // append id
            c.id = _c.id;
            handleSubCategory(c, null);
          })));
        }

        // existing category
        else {

          if (c.name !== originalCategory.name) {
            categoryOrderObs.push({
              id: c.id,
              name: c.name,
              order: c.order
            });
          }
          handleSubCategory(c, originalCategory);
        }

      });

    };


    // when an order is changed, update order of all other items
    const handleCategoryOrders = () => {
      this.categories.forEach((c) => {
        const originalCategory = this.originalCategoriesMapById.has(c.id) && this.originalCategoriesMapById.get(c.id)
        if (!originalCategory || c.order !== originalCategory.order) {
          categoryOrderObs.push({
            id: c.id,
            order: c.order,
          });
        }
      });
    };

    const handleSubCategory = (c, originalCategory) => {

      const handleSubCategoryOrders = (sc, c, originalCategory) => {
        // when an order is changed, update order of all other items
        let originalSubcategory = this.originalSubcategoriesMapByOriginalCatId.has(originalCategory.id)
                              && this.originalSubcategoriesMapByOriginalCatId.get(originalCategory.id).has(sc.id)
                              && this.originalSubcategoriesMapByOriginalCatId.get(originalCategory.id).get(sc.id)

        if (!originalSubcategory || sc.order !== originalSubcategory.order) {
          subCategoryOrderObs.push({
            id: sc.id,
            order: sc.order,
          });
        }
      };

        c.subcategories && c.subcategories.forEach((sc) => {

        let originalSubcategory = this.originalSubcategoriesMapByOriginalCatId.has(originalCategory.id)
        && this.originalSubcategoriesMapByOriginalCatId.get(originalCategory.id).has(sc.id)
        && this.originalSubcategoriesMapByOriginalCatId.get(originalCategory.id).get(sc.id)

        //find original Subcategory info
        if (!originalSubcategory)
        originalSubcategory = this.originalSubcategoriesMapByOriginalCatId.has(originalCategory.id)
                              && this.originalSubcategoriesMapByOriginalCatId.get(originalCategory.id).has(sc.id)
                              && this.originalSubcategoriesMapByOriginalCatId.get(originalCategory.id).get(sc.id)

        // new sc
        if (!sc.id) {
          subCategoryObs.push(this.subcategoryService.addSubcategory({
            name: sc.name,
            categoryId: c.id
          }).pipe(map((_sc: any) => {
            // append id to tree
            sc.id = _sc.id;
            handleSubCategoryOrders(sc, c, originalCategory);
            handleTabQuestions(null, sc);
            handleSubGroups(sc, null);
          })));

        } else {
          // existing sc
          if (sc.name !== originalSubcategory.name || sc.categoryId !== originalSubcategory.categoryId) {
            subCategoryOrderObs.push({
              id: sc.id,
              name: sc.name,
              categoryId: sc.categoryId
            });
          }

          handleSubCategoryOrders(sc, c, originalCategory);
          handleTabQuestions(originalSubcategory, sc);
          handleSubGroups(sc, originalSubcategory);
        }

      });

    };

    const handleTabQuestions = (originalSubcategory, sc) => {

      if (sc.tabQuestion) {
        const originalTabQuestion = originalSubcategory ? originalSubcategory.tabQuestion : null;

        // existing question used as tab
        if (sc.tabQuestion) {
          // tab question has changed
          if (!originalTabQuestion || sc.tabQuestion.id !== originalTabQuestion.id) {
            // associate new q to sc
            subcategoryTabQuestionObs.push(this.subcategoryService.createSubcategoryQuestionAssociation(sc.id, {
              questionId: sc.tabQuestion.id
            }));
          }
        }
      }

    };

    const handleSubGroups = (sc, originalSubcategory) => {

      const handleSubGroupOrders = (sg, originalSubgroup) => {
        if (!originalSubgroup || sg.order !== originalSubgroup.order
          || sc.id !== originalSubgroup.subgroupSubcategoryOrder.subcategoryId) {
          subGroupOrderObs.push({
            subcategoryId: sc.id,
            subgroupId: sg.id,
            order: sg.order
          });
        }
      };

      sc.subgroups && sc.subgroups.forEach(sg => {

        let originalSubgroup, originalSubcategoryId;
        originalSubcategory && originalSubcategory.subgroups.forEach(_sg => {
          if (_sg.id === sg.id) originalSubgroup = _sg;
        });

        // find original Subgroup info
        if (!originalSubgroup) {
          this.originalCategories.forEach(item => {
            item.subcategories.forEach(subItem => {
              subItem.subgroups.forEach(sgItem => {
                if (sgItem.id === sg.id) {
                  originalSubgroup = sgItem;
                  originalSubcategoryId = sgItem.subgroupSubcategoryOrder;
                }
              })
            })
          })
        }
        // new subgroup
        if (!sg.id) {
          // first need to add subgroup then do another post request to attach it to the subcategory using updateSubgroup_2
          subGroupObs.push(this.subgroupService.addSubgroup({
            name: sg.name,
            folderPath: sg.folderPath,
            docRegexPattern: sg.docRegexPattern,
            hash: sg.hash,
          }).pipe(map((_sg: any) => {

            // append id
            sg.id = _sg.id;

            subGroupAssociationObs.push(this.subcategoryService.createSubgroupAssociationFromSubcategory(sc.id, {
              subgroupId: _sg.id
            }));
            handleSubGroupOrders(sg, originalSubgroup);
            handleQuestions(sg, null, sc);
          })));

        }

        // existing sg
        else {
          if (sg.name !== originalSubgroup.name || sg.hash !== originalSubgroup.hash
            || sg.folderPath !== originalSubgroup.folderPath || sg.docRegexPattern !== originalSubgroup.docRegexPattern) {
            subGroupObs.push(this.subgroupService.updateSubgroup({
              id: sg.id,
              name: sg.name,
              folderPath: sg.folderPath,
              docRegexPattern: sg.docRegexPattern,
              hash: sg.hash,
            }));
          }
          if (originalSubcategoryId && sg.subgroupSubcategoryOrder.subcategoryId !== originalSubcategoryId.subcategoryId) {
            originalSubgroup.questions.forEach(q => {
              questionSubCatOrderObjects.push({
                questionId: q.id,
                subcategoryId: sg.subgroupSubcategoryOrder.subcategoryId,
              });
            });
          }

          handleSubGroupOrders(sg, originalSubgroup);
          handleQuestions(sg, originalSubgroup, sc);
        }

      });

    };

    const handleQuestions = (sg, originalSubgroup, sc) => {
      // question needs to be associated to subgroup before ordering
      const handleQuestionOrders = () => {

        sg.questions.forEach(q => {
          const originalQuestion = originalSubgroup && originalSubgroup.questions?.find(_q => _q.id === q.id);
          if ((!originalQuestion || q.order !== originalQuestion.order) && !questionOrderObjects.some(qo => qo.questionId === q.id)) {
            questionOrderObjects.push({
              questionId: q.id,
              subgroupId: sg.id,
              order: q.order,
            });
          }
        });
      };

      // check for questions added to subgroup
      const scsgAssociations: QuestionAssociation[] = [];
      sg.questions.forEach(q => {
        let hasBeenAdded = !originalSubgroup || !originalSubgroup.questions.find(_q => _q.id === q.id);
        let originalQuestion, originalSubcategoryId;
        if (hasBeenAdded) {
          this.originalCategories.forEach(item => {
            item.subcategories.forEach(subItem => {
              subItem?.subgroups?.forEach(sgItem => {
                sgItem.questions.forEach(qItem => {
                  if (qItem.id === q.id) {
                    hasBeenAdded = false;
                    originalQuestion = qItem;
                    originalSubcategoryId = sgItem.subgroupSubcategoryOrder
                  }
                });
              });
            });
          });
        }
        if (hasBeenAdded) {
          const association: QuestionAssociation = {};
          association.questionId = q.id;
          association.subGroupId = sg.id;
          association.subCategoryId = sc.id;
          scsgAssociations.push(association);
        }else {
          if (originalQuestion && sg.id !== originalQuestion.subGroupOrder.subgroupId) {
            questionOrderObjects.push({
              questionId: q.id,
              subgroupId: sg.id,
              order: q.order,
            });
          }
          if (originalSubcategoryId && sg.subgroupSubcategoryOrder.subcategoryId !== originalSubcategoryId.subcategoryId) {
            questionSubCatOrderObjects.push({
              questionId: q.id,
              subcategoryId: sg.subgroupSubcategoryOrder.subcategoryId,
            });
          }
        }
      });
      if (scsgAssociations.length) {
        questionObs.push(this.subgroupService.bulkAssociateSubgroupSubcategoryQuestion({ taxReturnTypeId: this.taxReturnTypeId, associations: scsgAssociations }))
      }

      handleQuestionOrders();
    };

    const handleCategoryObs = () => {

      const handleCategoryOrderObs = () => {
        if (categoryOrderObs.length > 0) {
          this.categoryService.updateCategory({categories: categoryOrderObs}).subscribe(() => {
            handleSubCategoryObs();
          },
            () => this.isSaving = false);
        } else {
          handleSubCategoryObs();
        }
      };

      if (categoryObs.length > 0) {
        forkJoin(categoryObs).subscribe((d) => {
          handleCategoryOrders();
          handleCategoryOrderObs();
        },
          () => this.isSaving = false);
      }
      else {
        handleCategoryOrders();
        handleCategoryOrderObs();
      }

    };

    const handleSubCategoryObs = () => {

      const handleSubCategoryOrderObs = () => {
        if (subCategoryOrderObs.length > 0) {
          this.subcategoryService.updateSubcategory({subCategories: subCategoryOrderObs}).subscribe(() => {
            handleSubGroupObs();
          },
            () => this.isSaving = false);

        } else {
          handleSubGroupObs();
        }
      };

      if (subCategoryObs.length > 0) {
        forkJoin(subCategoryObs).subscribe(() => {
          handleSubCategoryOrderObs();
        },
          () => this.isSaving = false);
      } else {
        handleSubCategoryOrderObs();
      }
    };

    const handleSubGroupObs = () => {

      const handleSubGroupAssociationObs = () => {
        if (subGroupAssociationObs.length > 0) {
          forkJoin(subGroupAssociationObs).subscribe(() => {
            handleSubGroupOrderObs();
          },
            () => this.isSaving = false);
        } else {
          handleSubGroupOrderObs();
        }
      };

      const handleSubGroupOrderObs = () => {
        if (subGroupOrderObs.length > 0) {
          this.subgroupService.updateSubgroupOrderInSubcategory({subgroupOrders: subGroupOrderObs}).subscribe(() => {
            handleQuestionObs();
          },
            () => this.isSaving = false);
        } else {
          handleQuestionObs();
        }
      };

      if (subGroupObs.length > 0) {
        forkJoin(subGroupObs).subscribe(() => {
          handleSubGroupAssociationObs();
        },
          () => this.isSaving = false);
      }
      else {
        handleSubGroupAssociationObs();
      }

    };

    const handleQuestionObs = () => {

      const handleQuestionOrderObs = () => {
        if (questionOrderObjects.length > 0) {
          questionOrderObs.push(this.subgroupService.updateQuestionOrderInSubgroup({questionOrders: questionOrderObjects}));
        }
        if (questionSubCatOrderObjects.length > 0) {
          questionOrderObs.push(this.subcategoryService.updateQuestionInSubcategoryOrder({questionOrders: questionSubCatOrderObjects}));
        }
        if (questionOrderObs.length > 0) {
          forkJoin(questionOrderObs).subscribe(() => {
            handleQuestionToSubgroupObs();
          },
            () => this.isSaving = false);
        } else {
          handleQuestionToSubgroupObs();
        }
      };

      const handleQuestionToSubgroupObs = () => {
        if (questionToSubgroupObs.length > 0) {
          forkJoin(questionToSubgroupObs).subscribe(() => {
            handleTabQuestionObs();
          },
            () => this.isSaving = false);
        } else {
          handleTabQuestionObs();
        }
      };

      if (questionObs.length > 0) {

        forkJoin(questionObs).subscribe(() => {
          handleQuestionOrderObs();
        },
          () => this.isSaving = false);

      } else {
        handleQuestionOrderObs();
      }

    };

    const handleTabQuestionObs = () => {
      if (subcategoryTabQuestionObs.length > 0) {
        forkJoin(subcategoryTabQuestionObs).subscribe(() => {
          this.init();
        },
          () => this.isSaving = false);
      } else {
        // only re-init if there are changes
        const hasChanges = [].concat(
          categoryObs,
          categoryOrderObs,
          subCategoryObs,
          subCategoryOrderObs,
          subcategoryTabQuestionObs,
          subGroupObs,
          subGroupAssociationObs,
          subGroupOrderObs,
          questionToSubgroupObs,
          questionObs,
          questionOrderObs).length > 0;
        if (hasChanges) this.init();
        else this.isSaving = false;
      }
    };

    handleCategory();
    handleCategoryObs();

  }

  expandAll() {
    this.categories.forEach(c => {
      if (c.isCollapsed){this.editNode(c)}
      c.subcategories.forEach(sc => {
        if (sc.isCollapsed || sc.isCollapsed === undefined) { this.editNode(null,sc) }
        sc.subgroups.forEach(sg => {
          if (sg.isCollapsed) { this.editNode(null,null,sg) }
        })
      })
    })
  }
  collapseAll() {
    this.categories.forEach(c => {
      if (!c.isCollapsed){this.editNode(c)}
      c.subcategories.forEach(sc => {
        if (!sc.isCollapsed) { this.editNode(null,sc) }
        sc.subgroups.forEach(sg => {
          if (!sg.isCollapsed) { this.editNode(null,null,sg) }
        })
      })
    })
  }

}
